import { useEffect } from 'react'
import * as Yup from 'yup'
import { Form, useFormikContext } from 'formik'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Box, Grid, Paper, Typography } from '@dhl/theme'
import { BarcodeInputDeprecated } from '@dhl/barcode-input'
import { SubmitButton } from '@dhl/components'
import { dataLayerPublish } from '@dhl/datalayer'

import { ParcelTypeInput, createParcelTypeValidationSchema } from './inputs/ParcelTypeInput'
import { type Basket } from '~/basket/model/reducers'
import { type UpdatePackageOptions } from '~/basket/model/session/actions'
import { type Package } from '~/basket/model/session/reducers'
import { FieldEffect } from '~/components/FieldEffect'
import { CarrierInput } from './inputs/CarrierInput'
import { DestinationCountryInput } from './inputs/DestinationCountryInput'
import { createTrackerCodeValidationSchema } from './inputs/TrackerCodeInput'

export type PackageValidationSchemaOptions = {
  isTrackerCodeRequired: boolean
}

export const createPackageValidationSchema = (options: PackageValidationSchemaOptions) => (t: TFunction) =>
  Yup.object().shape({
    trackerCode: createTrackerCodeValidationSchema(options.isTrackerCodeRequired)(t),
    destinationCountry: Yup.string().required(),
    carrierKey: Yup.string().required(),
    parcelTypeKey: createParcelTypeValidationSchema(t),
  }) as Yup.Schema<Partial<Package>>

type Props = {
  basket: Basket
  isLoading?: boolean
  updatePackage: (values: Partial<Package>, options: UpdatePackageOptions) => void
}

export const PackageForm = ({ basket, isLoading, updatePackage }: Props) => {
  const { t } = useTranslation()
  const { setFieldValue, handleSubmit } = useFormikContext()

  // When changing the destination country, possibly the selectable carriers change (and therefore the selectable package types).
  // Since initialValues on Formik have already been set, Formik doesn't know about the change. Hence, we force it.
  useEffect(() => {
    setFieldValue('carrierKey', basket.package.carrierKey)
  }, [basket.package.carrierKey, setFieldValue])

  useEffect(() => {
    setFieldValue('parcelTypeKey', basket.package.parcelTypeKey)
  }, [basket.package.parcelTypeKey, setFieldValue])

  return (
    <Form data-test="package-form">
      <FieldEffect
        select={(values: Package) => values.destinationCountry}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={({ values }: any) => {
          if (values && values.destinationCountry) {
            dataLayerPublish('CLICK', {
              action: 'change-destination-country',
              key: 'Destination country',
              value: values.destinationCountry,
            })
            updatePackage(values, { hasUpdatedDestinationCountry: true })
          }
        }}
      />

      <FieldEffect
        select={(values: Package) => values.carrierKey}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={({ values }: any) => {
          dataLayerPublish('CLICK', {
            action: 'change-carrier-key',
            key: 'Carrier',
            value: values.carrierKey,
          })
          updatePackage(values, { hasUpdatedCarrierKey: true })
        }}
      />

      <FieldEffect
        select={(values: Package) => values.parcelTypeKey}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={({ values }: any) => {
          if (values && values.parcelTypeKey) {
            dataLayerPublish('CLICK', {
              action: 'change-parcel-typr',
              key: 'Parcel Type',
              value: values.parcelTypeKey,
            })
            updatePackage(values, { hasUpdatedParcelTypeKey: true })
          }
        }}
      />

      <Grid container spacing={2} wrap="nowrap" direction="column">
        <Grid>
          <Box mt={2}>
            <Typography variant="h5">{t('select_destination_title')}</Typography>
          </Box>
        </Grid>

        {basket.config.isTrackerCodeEnabled && (
          <Grid>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <BarcodeInputDeprecated name="trackerCode" />
              </Box>
              <SubmitButton onClick={handleSubmit} />
            </Box>
          </Grid>
        )}

        <Grid>
          <DestinationCountryInput
            name="destinationCountry"
            selectableDestinationCountries={basket.selectableDestinationCountries}
          />
        </Grid>

        {basket.selectableCarriers.length > 1 && (
          <>
            <Grid>
              <Box mt={2}>
                <Typography variant="h5">{t('select_transporttype')}</Typography>
              </Box>
            </Grid>
            <Grid>
              <CarrierInput name="carrierKey" selectableCarriers={basket.selectableCarriers} />
            </Grid>
          </>
        )}

        {!isLoading && (
          <>
            {basket.selectableCarriers.length > 0 && (
              <>
                <Grid>
                  <Box mt={2}>
                    <Typography variant="h5">{t('select_parceltype')}</Typography>
                  </Box>
                </Grid>
                <Grid>
                  <ParcelTypeInput
                    key={`${basket.package.destinationCountry}-${basket.package.carrierKey}`.toLowerCase()}
                    name="parcelTypeKey"
                    destinationCountry={basket.package.destinationCountry}
                    parcelTypes={basket.selectableParcelTypes}
                  />
                </Grid>
              </>
            )}

            {basket.selectableCarriers.length === 0 && (
              <Grid>
                <Paper>
                  <Box p={2} data-test="non-selectable-carrier">
                    {t('no_carrier_available')}
                  </Box>
                </Paper>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Form>
  )
}
